import axios from "axios";
import { ROUTES } from "../routes/routes.constants";
import { apiEndpoint } from "./commonApi";
import { removeModuleByKeySafe, setPermissionDenied } from "../redux/userDataSlice";
const dispatchToRedux = (action) => {
    try {
        const storeModule = require("../redux/store");
        storeModule?.store?.dispatch?.(action);
    }
    catch (e) {
        console.error("Redux dispatch skipped (store not ready yet)");
    }
};
const getUserDataFromRedux = () => {
    const storeModule = require("../redux/store");
    const state = storeModule?.store?.getState?.();
    return (state?.userData || {});
};
const getAuthTokenFromRedux = () => {
    try {
        const storeModule = require("../redux/store");
        const state = storeModule?.store?.getState?.();
        return state?.auth?.token || null;
    }
    catch (e) {
        return null;
    }
};
const isAuthApiCall = (path) => {
    return path.includes("/api/auth/");
};
const isModulesApiCall = (path) => {
    return path.includes("/api/modules");
};
let axiosInstances = {};
const createInstance = (timeoutVal) => {
    const controller = new AbortController();
    const customAxiosInstance = {
        controller,
        instance: axios.create({
            headers: {},
            signal: controller.signal,
            timeout: timeoutVal
        })
    };
    return customAxiosInstance;
};
const getInstance = (pathname, timeout) => {
    const existingInstance = axiosInstances[pathname];
    if (existingInstance && !existingInstance.controller.signal.aborted) {
        return existingInstance.instance;
    }
    const customAxiosInstance = createInstance(timeout);
    axiosInstances[pathname] = customAxiosInstance;
    return customAxiosInstance.instance;
};
export const abortAllApiCalls = () => {
    Object.values(axiosInstances).forEach(obj => {
        obj.controller.abort();
    });
    axiosInstances = {};
};
const renewToken = () => {
    localStorage.clear();
    window.location.href = ROUTES.login.path;
};
const redirectToNotFound = (errorMessage) => {
    // Dispatch to Redux to show "Access Denied" in DashboardLayout without changing URL
    dispatchToRedux(setPermissionDenied({ message: errorMessage }));
};
export const ApiCall = async (method, path, payload, header, timeout = 7 * 60 * 1000, responseType = "json", signal) => {
    try {
        const authApi = isAuthApiCall(path);
        const modulesApi = isModulesApiCall(path);
        // If a module is active but modules are not loaded yet,
        // we wait until fetchModules succeeds (after-login rule).
        if (!authApi && !modulesApi) {
            const { activeModuleKey, modulesLoaded } = getUserDataFromRedux();
            if (activeModuleKey && !modulesLoaded) {
                const maxWaitMs = 10000;
                const pollMs = 100;
                const start = Date.now();
                while (Date.now() - start < maxWaitMs) {
                    const latest = getUserDataFromRedux();
                    if (latest?.modulesLoaded)
                        break;
                    await new Promise((r) => setTimeout(r, pollMs));
                }
            }
        }
        const instance = getInstance(`${window.location.pathname}${window.location.search}`, timeout);
        const hasPayload = payload !== undefined && payload !== null && payload !== "";
        const isFormData = payload instanceof FormData;
        const { activeModuleKey } = getUserDataFromRedux();
        const moduleKeyHeader = !authApi && !modulesApi && activeModuleKey ? { 'x-module-key': activeModuleKey } : {};
        const response = await instance({
            method,
            url: apiEndpoint + path,
            signal,
            responseType,
            data: hasPayload && method.toUpperCase() !== 'GET'
                ? payload
                : undefined,
            params: hasPayload && method.toUpperCase() === 'GET'
                ? payload
                : undefined,
            timeout,
            headers: {
                ...moduleKeyHeader,
                ...header,
                ...(isFormData ||
                    (!hasPayload || method.toUpperCase() === 'GET')
                    ? {}
                    : { "Content-Type": "application/json" }),
            },
        });
        // ✅ Loop Prevention: Only one refresh should happen at a time
        const currentToken = getAuthTokenFromRedux();
        const requestToken = header?.Authorization || header?.authorization;
        if (response?.headers?.['x-action-refresh-user'] === 'true' &&
            path !== '/api/auth/me' &&
            !window.isRefreshingUser &&
            (!currentToken || !requestToken || currentToken === requestToken)) {
            window.isRefreshingUser = true; // Set lock
            const { updateUserAndToken } = require("../redux/authSlice");
            const { fetchModules } = require("../redux/userDataSlice");
            instance({
                method: 'GET',
                url: apiEndpoint + '/api/auth/me',
                headers: { ...header, ...moduleKeyHeader }
            }).then(res => {
                if (res?.data?.success) {
                    dispatchToRedux(updateUserAndToken({
                        user: res.data.data,
                        token: res.data.token,
                        permissions: res.data.data.permissions || {}
                    }));
                    dispatchToRedux(fetchModules());
                }
            }).catch(() => {
                // Silently fail but release lock
            }).finally(() => {
                window.isRefreshingUser = false; // Release lock
            });
        }
        return response;
    }
    catch (error) {
        const axiosError = error;
        // Silently return for aborted / cancelled requests to avoid race condition noise
        if (axios.isCancel(error) ||
            error?.name === 'CanceledError' ||
            error?.name === 'AbortError' ||
            error?.code === 'ERR_CANCELED') {
            return undefined;
        }
        console.error('error', error);
        // Handle 403 Forbidden - Unauthorized Access
        if (axiosError?.response?.status === 403) {
            const moduleKey = error.config?.headers?.modulekey;
            console.error('moduleKey', moduleKey);
            dispatchToRedux(removeModuleByKeySafe(moduleKey));
            const errorMessage = axiosError?.response?.data?.message ||
                "Unauthorized Access: User do not have permission to access this resource. Contact Admin.";
            // Only a module-level access denial (no access to this module at all, tagged by
            // authMiddleware) should blank the whole page. A 403 from a per-client/per-resource
            // check inside a controller (e.g. "you can't view this specific client's data")
            // should just fail that one request — it used to trip the same global lockout,
            // stranding the page on "Access Restricted" even though the user could use the page.
            if (axiosError?.response?.data?.reason === 'module_access_denied') {
                redirectToNotFound(errorMessage);
            }
            return axiosError;
        }
        if (axiosError?.response?.status === 401) {
            renewToken();
        }
        if (axiosError.message === 'Network Error') {
            console.error(`${axiosError}, Server is not responding, please try again after some time`);
        }
        if (axiosError.response?.data?.statusCode === 401 && header && !header['access-token']) {
            if (axiosError.response.data.access_expire) {
                renewToken();
            }
            else if (axiosError.response.data.refresh_expire) {
                return axiosError.response;
            }
        }
        else {
            return axiosError;
        }
    }
};
